import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {forkJoin, Subject} from 'rxjs';
import {debounceTime, finalize, take, takeUntil} from 'rxjs/operators';
import {MatSort, Sort} from '@angular/material/sort';
import {ActivatedRoute, Router} from '@angular/router';
import {SelectionModel} from '@angular/cdk/collections';
import {MatTableDataSource} from '@angular/material/table';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatDialog} from '@angular/material/dialog';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {Common, getSortDirection, sortByPropertyAndDirection} from '../../../../common/util/Util';
import {DateUtil} from '../../../../common/util/DateUtil';
import {CreateProviderDTOFromProviderUploadDTO, ProviderDTO} from '../../../models/ProviderDTO';
import {CaseFuneralDTO} from '../../../../clm/models/CaseFuneralDTO';
import {PagedRequestDTO} from '../../../../common/models/PagedRequestDTO';
import {ProviderNetworkDTO} from '../../../models/ProviderNetworkDTO';
import {LookupService} from '../../../../common/services/lookup.service';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {TableControl} from '../../../../common/models/TableControl';
import {SharedService} from '../../../../common/services/shared-service';
import {PagedResponseDTO} from '../../../../common/models/PagedResponseDTO';
import {LookupValue} from '../../../../clm/types/lookup-value.enum';
import {EntRequestBuilder} from '../../../services/ent-request-builder';
import {ProviderService} from '../../../services/provider.service';
import {ProviderNetworkService} from '../../../services/provider-network.service';
import {FileService} from '../../../../common/services/file.service';
import {ProviderUploadDTO} from '../../../models/ProviderUploadDTO';
import {GenericListDialogComponent} from '../../../../common/dialogs/generic-list-dialog/generic-list-dialog.component';
import {ProviderNetworkLinkDTO} from '../../../models/ProviderNetworkLinkDTO';
import {detailsExpand} from '../../../../common';
import {DisplayStrings} from '../../../../common/constants/DisplayStrings';
import {FilterDataService} from 'src/app/modules/common/services/filter-data.service';
import {EntityTable} from 'src/app/modules/common/types/entity-table.enum';
import {UserAuthorisationService} from '../../../../common/services/user-authorisation.service';

enum Controls {
  Name = 'name',
  Code = 'code',
  Grading = 'grading',
  Administrator = 'administrator',
  StartDate = 'startDate',
  EndDate = 'endDate',
}

enum TableColumns {
  Select = 'select',
  ProviderId = 'providerId',
  PracticeNumber = 'practiceNumber',
  Name = 'name',
  BusinessType = 'businessType',
  EntityStatus = 'entityStatus',
  ProviderFacilityType = 'providerFacilityType',
  StartDate = 'startDate',
  EndDate = 'endDate'
}

enum RouteHeadings {
  Add = 'Add Provider Network',
  Edit = 'Edit Provider Network',
}

enum RoutePaths {
  Add = 'add-provider-network',
  Edit = 'edit-provider-network',
}

const PROVIDER_UPLOAD_TEMPLATE_FILE_NAME = 'Greydom Provider Upload Template.csv';

@Component({
  selector: 'app-add-provider-network-view',
  templateUrl: './add-provider-network-view.component.html',
  styleUrls: [
    'add-provider-network-view.component.scss',
    '../../../../../../styles/global/_add-view-edit-styles.scss'
  ],
  animations: [detailsExpand]
})
export class AddProviderNetworkViewComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly destroyed$ = new Subject();
  readonly dateFormat = DateUtil.dateFormat;
  readonly dateTimeFormat = DateUtil.dateTimeFormat;
  readonly routeHeadings = RouteHeadings;
  readonly routePaths = RoutePaths;
  readonly lookupValue = LookupValue;
  readonly controls = Controls;
  readonly tableColumns = TableColumns;
  readonly tabName: string;

  backPageName: string;
  heading: string;
  isEditMode: boolean;
  isLoadingAllProviders: boolean;
  providerNetworkId: number;

  // General Step:
  administratorLoopUpValues: NameValueDTO[];
  productNetworkFormGroup: FormGroup;
  providerNetworkDTO: ProviderNetworkDTO;

  // MatPaginator Inputs
  @ViewChild('providerNetworksPaginator', {static: false}) providerNetworksPaginator: MatPaginator;

  // TextArea Search fields
  practiceNumberSearch = '';
  providerNameSearch = '';

  // Datasource attributes
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('providerNetworksPaginator') paginator: MatPaginator;
  @ViewChild('toggleSelectedProviders', { static: false }) private toggleSelectedProviders: MatSlideToggle;

  readonly filterDataValueTextChanged$ = new Subject<boolean>();

  providerExpandedRow: ProviderDTO | null;
  providerDataSource: MatTableDataSource<ProviderDTO> = new MatTableDataSource(); // Data source
  providerTableControl: TableControl;

  selectedProviders: ProviderDTO[] = [];
  selectedProviderNetworkLinkDTO: ProviderNetworkLinkDTO[] = [];
  allProviders: ProviderUploadDTO[] = [];
  selectionControl = new SelectionModel<ProviderDTO>(true, []);
  isProvidersEntryExists: boolean;
  providerDisplayedColumns: string[] = Object.values(TableColumns);

  private fieldMapHashMap = {};
  private loggedInUserName: string;

  constructor(
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private lookupService: LookupService,
    private providerService: ProviderService,
    private providerNetworkService: ProviderNetworkService,
    private route: ActivatedRoute,
    private router: Router,
    private sharedService: SharedService,
    private fileService: FileService,
    private filterDataService: FilterDataService,
    private userAuthorisationService: UserAuthorisationService
  ) {
    this.providerNetworkDTO = null;
    this.tabName = 'provider-network';
    this.backPageName = 'enterprise-management';
    this.providerTableControl = this.getDefaultTableControls();

    if (this.route.snapshot.url[1].path === this.routePaths.Edit) {
      this.heading = this.routeHeadings.Edit;
      this.isEditMode = true;
    }
    else if (this.route.snapshot.url[1].path === this.routePaths.Add) {
      this.heading = this.routeHeadings.Add;
      this.isEditMode = false;
    }

    this.isLoadingAllProviders = false;
  }

  get bannerText(): string {
    let bannerText = '';
    if (!this.isAllowedToSubmitProviderNetwork()) {
      bannerText = DisplayStrings.permissionsErrorMessages.rolePermissions.roleEnterpriseManager;
    }
    return bannerText;
  }

  getDefaultTableControls(): TableControl {
    const controls = EntRequestBuilder.getTableControls();
    return new TableControl(
      controls.length,
      controls.pageSize,
      controls.pageNumber,
      controls.pageSizeOptions,
      'providerId',
      'DESCENDING',
      'name',
      ''
    );
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.initLookUpValues();
    this.getHelpInfo('Provider Network');
    this.initProductNetworkFormControls();
    this.getListOfProviders(this.providerTableControl);

    if (this.route.snapshot.url[1].path === this.routePaths.Edit) {
      this.getProviderNetworkByProviderNetworkId(this.providerNetworkId);
    }
  }

  ngAfterViewInit(): void {
    this.toggleSelectedProviders.checked = this.isEditMode;
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initSubscriptions() {

    // Route Subscription
    this.route.params.subscribe(params => {
      this.providerNetworkId = params.id;
    });

    // LoggedIn User Subscription
    this.sharedService.getLoggedInProfile$()
      .pipe(
        take(1)
      )
      .subscribe(() => {
        // Note: Won't be using profile obj instead invoke `getLoggedInUserName` as username that is already set in service.
        this.loggedInUserName = this.sharedService.getLoggedInUserName();
      });


    this.filterDataValueTextChanged$
      .pipe(
        takeUntil(this.destroyed$),
        debounceTime(this.debounceTime)
      )
      .subscribe(() => {
          const pagedRequestDTO = EntRequestBuilder.getPagedRequestDTO(this.providerTableControl, EntityTable.TblProvider);
          pagedRequestDTO.fieldMap = this.fieldMapHashMap; // override default `fieldMap` object with multiple fields:
          this.getListOfProvidersByPageRequestDTO(pagedRequestDTO);
        });

    this.selectionControl.changed
      .subscribe(data => {
        const added = data.added;
        const removed = data.removed;
        if (added.length > 0) {
          this.addSelectedProviders(added);
        }
        if (removed.length > 0) {
          this.removeSelectedProviders(removed);
        }
      });
  }

  addSelectedProviders(providers: ProviderDTO[]): void {
    for (const provider of providers) {
      const isProviderExists = this.selectedProviders.some((item: ProviderDTO) => item.providerId === provider.providerId);
      if (!isProviderExists) {
        this.selectedProviders.push(provider);
      }
    }
  }

  removeSelectedProviders(providers: ProviderDTO[]): void {
    if (providers.length === 1) {
      const provider = providers[0];
      const index = this.selectedProviders.findIndex((item: ProviderDTO) => item.providerId === provider.providerId);
      if (index !== -1) {
        this.selectedProviders.splice(index, 1);
      }
    }
    else {
      this.clearSelectedProviders();
    }
  }

  initLookUpValues() {
    forkJoin(
      this.lookupService.getListOfLookupValues(this.lookupValue.Administrator)
    )
      .pipe(
        take(1)
      )
      .subscribe(
        (responses: any) => {
          if (responses[0]) {
            this.administratorLoopUpValues = responses[0];
          }
        });
  }

  initProductNetworkFormControls() {
    this.productNetworkFormGroup = this.formBuilder.group({
      [this.controls.Name]: new FormControl('', [Validators.required]),
      [this.controls.Code]: new FormControl('', [Validators.required]),
      [this.controls.Grading]: new FormControl(''),
      [this.controls.Administrator]: new FormControl('', [Validators.required]),
      [this.controls.StartDate]: new FormControl('', [Validators.required]),
      [this.controls.EndDate]: new FormControl('')
    });
  }

  getHelpInfo(componentName: string) {
    this.sharedService.getHelpInfo(componentName);
  }

  getInfoTooltip(inputName: string): string {
    return this.sharedService.getInfoTooltip(inputName);
  }

  onInputChangedFilterData(event: Event, filterField: string) {
    const filterValue = (event.target as HTMLInputElement).value;

    this.providerNetworksPaginator.firstPage();
    this.providerTableControl.pageNumber = 0;

    this.fieldMapHashMap = {...this.filterDataService.getFieldMapHashMap(filterField, filterValue, this.fieldMapHashMap)};

    if (this.toggleSelectedProviders.checked) {
      this.providerDataSource = new MatTableDataSource(this.filterSelectedProviders());
    }
    else {
      this.filterDataValueTextChanged$.next(true);
    }

    this.providerTableControl.filterField = filterField;
    this.providerTableControl.filterValue = filterValue;
  }

  filterSelectedProviders(): ProviderDTO[] {
    return this.filterDataService.filterSelectedItemsBasedOnFieldMap<ProviderDTO>(this.selectedProviders, this.fieldMapHashMap);
  }

  pageChanged($event: PageEvent) {
    if (!this.toggleSelectedProviders.checked) {
      this.providerTableControl.pageNumber = $event.pageIndex;
      this.providerTableControl.pageSize = $event.pageSize;
      this.getListOfProviders(this.providerTableControl);
    }
  }

  sortData($event: Sort) {
    this.providerNetworksPaginator.firstPage();
    this.providerTableControl.pageNumber = 0;

    if (!this.toggleSelectedProviders.checked) {
      this.providerTableControl.sortField = this.getCorrectSortFieldName($event.active, false);
      this.providerTableControl.filterField = null;
      this.providerTableControl.filterValue = null;
      this.providerTableControl.sortDirection = getSortDirection($event.direction);

      // Manipulate/Override default `fieldMap` object with multiple fields:
      const pagedRequestDTO = EntRequestBuilder.getPagedRequestDTO(this.providerTableControl, EntityTable.TblProvider);
      pagedRequestDTO.fieldMap = this.fieldMapHashMap;
      this.getListOfProvidersByPageRequestDTO(pagedRequestDTO);
    }
    else {
      let propertyToSortOn = this.getCorrectSortFieldName($event.active, true);
      this.providerDataSource = new MatTableDataSource(
        this.filterSelectedProviders().sort(sortByPropertyAndDirection(propertyToSortOn, $event.direction))
      );
    }
  }

  getCorrectSortFieldName(columnName: string, sortingSelected: boolean) : string {
    if (columnName === this.tableColumns.BusinessType) {
      return sortingSelected ? `${columnName}.name` : 'businessTypeByBusinessTypeId.name';
    } else if (columnName === this.tableColumns.EntityStatus) {
      return sortingSelected ? `${columnName}.name` : 'entityStatusByEntityStatusId.name';
    } else if (columnName === this.tableColumns.ProviderFacilityType) {
      return sortingSelected ? `${columnName}.description` : 'providerFacilityTypeByProviderFacilityTypeId.description';
    } else {
      return columnName;
    }
  }

  resetProviderNetworkFormGroup() {
    this.productNetworkFormGroup.reset();
  }

  toggleSelect(providerDTO: ProviderDTO) {
    this.selectionControl.toggle(providerDTO);
    this.IsProvidersEntryExist();
  }

  resetProviderState() {
    this.clearSelection();
    this.clearSelectedProviders();
    this.IsProvidersEntryExist();

    this.providerNameSearch = '';
    this.practiceNumberSearch = '';

    // reset `fieldMap` filters
    this.fieldMapHashMap = null;
    this.providerTableControl = this.getDefaultTableControls(); // reset to initial state
    this.filterDataValueTextChanged$.next(true);
  }

  clearSelection() {
    this.selectionControl.clear();
  }

  clearSelectedProviders() {
    this.selectedProviders = [];
  }

  isSelected(providerDTO: ProviderDTO): boolean {
    return this.selectionControl.isSelected(providerDTO);
  }

  saveProviderNetworkDTO() {
    const payload = this.getProviderNetworkDTOPayload();
    this.providerNetworkService.saveProviderNetwork(payload)
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response: CaseFuneralDTO | any) => {
          if (response) {
            if (this.isEditMode) {
              this.sharedService.openSnackBar(`Provider Network edited successfully`, 'Dismiss');
            }
            else {
              this.sharedService.openSnackBar(`Provider Network added successfully`, 'Dismiss');
            }
            this.resetState();
            this.router.navigate(['/enterprise-management'], {queryParams: {tab: this.tabName}});
          }
          else if (response && response.businessMessage) {
            this.sharedService.openSnackBar(`${response.businessMessage}`, 'Dismiss');
          }
        },
        error: (error) => {
          this.sharedService.openErrorDialog(error.message);
        }});
  }

  toggleShowSelectedProviders(cannotShowError?: boolean) {
    if (this.toggleSelectedProviders.checked) {
      if (this.selectedProviders && this.selectedProviders.length === 0) {
        if (!cannotShowError) {
          this.sharedService.openSnackBar(`Please select at least 1 provider`, 'Dismiss');
        }
        return;
      }
      else {
        this.providerDataSource = new MatTableDataSource<ProviderDTO>(
          this.filterSelectedProviders()
        );
        this.providerDataSource.sort = this.sort;
        this.providerDataSource.paginator = this.paginator;
        this.setSelectedProviders();
      }
    }
    else {
      this.providerDataSource = new MatTableDataSource();
      this.paginator.length = this.providerTableControl.length;
      this.getListOfProviders(this.providerTableControl);
    }
  }

  onUpdateProviderNetworkHeading(value: string) {
    this.heading = this.isEditMode ? `${this.routeHeadings.Edit} - ${value}` : `${this.routeHeadings.Add} - ${value}`
  }

  downloadFile() {
    this.getAllProviders();
    if (this.isLoadingAllProviders) {
      this.sharedService.openSnackBar('The list of providers are not yet ready - please remain on this page and try again in 1 minutes.', 'Dismiss');
      return;
    }
    const providerUploadDTOs: ProviderUploadDTO[] = [];
    // For each provider
    this.allProviders.forEach(provider => {
      let isSelected = false;
      // For each selected provider
      this.selectedProviders.forEach(selectedProvider => {
        // If the provider is selected
        if (provider.providerId === selectedProvider.providerId) {
          // Set the boolean to true
          isSelected = true;
        }
      });
      // Add to the list of upload DTOs
      provider.isSelected = isSelected;
      providerUploadDTOs.push(provider);
    });
    this.fileService.promptBrowserFileDownloadFromDataObject(providerUploadDTOs, PROVIDER_UPLOAD_TEMPLATE_FILE_NAME);
  }

  fileUploadListener(e) {
    this.getAllProviders();
    if (this.isLoadingAllProviders) {
      this.sharedService.openSnackBar('The list of providers are not yet ready - please remain on this page and try again in 1 minutes.', 'Dismiss');
      return;
    }
    const files: FileList = e.target.files;
    // If files were uploaded
    if (files && files.length > 0) {
      // Read the first (and only) file
      const file: File = files.item(0);
      const reader: FileReader = new FileReader();
      reader.readAsText(file);
      reader.onload = () => {
        const txt = reader.result as string;
        let lines = txt.split('\r\n');
        const delimiter = lines[0].includes(',') ? ',' : ';';
        lines = lines.filter(line => (line.trim() !== '' && line.trim() !== '""' && line.trim() !== '\"\"'));

        // Exit if not valid
        const isFileValid = this.isFileValid(lines, delimiter);
        if (!isFileValid) {
          e.target.value = '';
          return;
        }

        // Populate selected providers
        this.selectedProviders = [];
        lines.forEach(line => {
          const selectedProviderUploadDTO = this.allProviders.find(provider =>
            line.split(delimiter)[0]?.trim().toLowerCase() === 'true' &&
            provider.providerId === Number(line.split(delimiter)[1]?.trim())
          );
          if (selectedProviderUploadDTO) {
            const selectedProvider = CreateProviderDTOFromProviderUploadDTO(selectedProviderUploadDTO);
            this.selectedProviders.push(selectedProvider);
          }
        });
        this.setSelectedProviders();
        this.IsProvidersEntryExist();
        e.target.value = '';
        this.sharedService.openSnackBar('Providers imported successfully', 'Dismiss');
      };
    }
  }

  isFinishButtonDisabled(): boolean {
    let isDisabled = false;

    if (!this.isAllowedToSubmitProviderNetwork()) {
      isDisabled = true;
    }
    else if (!this.productNetworkFormGroup.valid) {
      isDisabled = true;
    }
    else if (!this.IsProvidersEntryExist()) {
      isDisabled = true;
    }
    else if (this.createdBySystem()) {
      isDisabled = true;
    }
    return isDisabled;
  }

  getFinishButtonTooltip(): string | null {
    let tooltip = DisplayStrings.generic.disabledButton;

    if (!this.isAllowedToSubmitProviderNetwork()) {
      tooltip = this.bannerText;
    }
    else if (!this.productNetworkFormGroup.valid) {
      tooltip = DisplayStrings.generic.requiredFields;
    }
    else if (!this.IsProvidersEntryExist()) {
      tooltip = 'Please select at least 1 provider';
    }
    else if (this.createdBySystem()) {
      tooltip = DisplayStrings.generic.externalSystem;
    }
    return tooltip;
  }

  getClearButtonTooltip(): string | null {
    if (!this.isAllowedToSubmitProviderNetwork()) {
      return DisplayStrings.permissionsErrorMessages.rolePermissions.rolEnterpriseManagerClearData;
    }
    return null;
  }

  isAllowedToSubmitProviderNetwork(): boolean {
    let hasAccessToSubmitProviderNetwork = false;
    const hasRoleOperationsManager = this.userAuthorisationService.hasRoleEnterpriseManager();

    if (hasRoleOperationsManager) {
      hasAccessToSubmitProviderNetwork = true;
    }
    return hasAccessToSubmitProviderNetwork;
  }

  private createdBySystem(): boolean {
    return this.providerNetworkDTO && this.providerNetworkDTO.createdBy.toLowerCase() === 'system';
  }

  private IsProvidersEntryExist() {
    return this.isProvidersEntryExists = !!(this.selectedProviders && this.selectedProviders.length > 0);
  }

  private getProviderNetworkByProviderNetworkId(providerNetworkId: number): void {
    this.providerNetworkService.getProviderNetworkByProviderNetworkId(providerNetworkId)
      .pipe(
        take(1)
      )
      .subscribe(
        (response: ProviderNetworkDTO) => {
          this.providerNetworkDTO = response;
          this.setFormsForEdit(response);
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private setFormsForEdit(providerNetworkDTO: ProviderNetworkDTO) {
    const {name, code, grading, administrator, startDate, endDate, providers} = providerNetworkDTO;
    this.productNetworkFormGroup.patchValue({
      [this.controls.Name]: name,
      [this.controls.Code]: code,
      [this.controls.Grading]: grading,
      [this.controls.Administrator]: administrator ? administrator.value : null,
      [this.controls.StartDate]: startDate ? new Date(startDate) : null,
      [this.controls.EndDate]: endDate ? new Date(endDate) : null
    });
    this.selectedProviderNetworkLinkDTO = providers ? providers : [];
    this.selectedProviders = providers ? providers.map((item: ProviderNetworkLinkDTO) => {
      const provider: ProviderDTO = item.provider;
      const providerNetworkLinkDTOExist = this.selectedProviderNetworkLinkDTO.find((item2) => item2.provider.providerId === provider.providerId);
      if (providerNetworkLinkDTOExist) {
        provider.startDate = providerNetworkLinkDTOExist.startDate;
        provider.endDate = providerNetworkLinkDTOExist.endDate;
      }
      else {
        provider.startDate = null;
        provider.endDate = null;
      }
      return provider;
    }) : [];

    this.onUpdateProviderNetworkHeading(providerNetworkDTO.name);
    this.IsProvidersEntryExist();

    // Reason:
    // `_getListOfProviders` may take more time to fetch list since it is async in nature
    // therefore explicit delay added to select values provides returned from BE
    setTimeout(() => {
      this.toggleShowSelectedProviders(true);
      this.setSelectedProviders();
    }, this.debounceTime);
  }

  private getListOfProviders(tableControl: TableControl): void {
    const pagedRequestDTO = EntRequestBuilder.getPagedRequestDTO(tableControl, EntityTable.TblProvider);
    pagedRequestDTO.fieldMap = this.fieldMapHashMap;
    this.providerService
      .getListOfProviders(pagedRequestDTO)
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe(
        (response: PagedResponseDTO<ProviderDTO>) => {
          let providersInTable = response.resultList;
          if (this.selectedProviderNetworkLinkDTO && this.selectedProviderNetworkLinkDTO.length > 0) {
            providersInTable = providersInTable.map((provider: ProviderDTO) => {
              const providerNetworkLinkDTOExist = this.selectedProviderNetworkLinkDTO.find((item) => item.provider.providerId === provider.providerId);
              if (providerNetworkLinkDTOExist) {
                provider.startDate = providerNetworkLinkDTOExist.startDate;
                provider.endDate = providerNetworkLinkDTOExist.endDate;
              }
              else {
                provider.startDate = null;
                provider.endDate = null;
              }
              return provider;
            });
          }
          this.providerDataSource.data = providersInTable;
          this.providerTableControl.length = response.totalRecords;
          this.setSelectedProviders();
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  // Note: handle by PageRequestDTO
  // - To cater multiple fieldMap
  // - directly pass PageRequestDTO as argument to parameter
  // - avoid manipulating `providerTableControl` -> 'fieldMap' so better to have a separate copy to fetch list

  private getListOfProvidersByPageRequestDTO(pagedRequestDTO: PagedRequestDTO): void {
    this.providerService
      .getListOfProviders(pagedRequestDTO)
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe(
        (response: PagedResponseDTO<ProviderDTO>) => {
          let providersInTable = response.resultList;
          if (this.selectedProviderNetworkLinkDTO && this.selectedProviderNetworkLinkDTO.length > 0) {
            providersInTable = providersInTable.map((provider: ProviderDTO) => {
              const providerNetworkLinkDTOExist = this.selectedProviderNetworkLinkDTO.find((item) => item.provider.providerId === provider.providerId);
              if (providerNetworkLinkDTOExist) {
                provider.startDate = providerNetworkLinkDTOExist.startDate;
                provider.endDate = providerNetworkLinkDTOExist.endDate;
              }
              else {
                provider.startDate = null;
                provider.endDate = null;
              }
              return provider;
            });
          }
          this.providerDataSource.data = providersInTable;
          this.providerTableControl.length = response.totalRecords;
          this.setSelectedProviders();
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private getAllProviders(): void {
    if (this.allProviders != null && this.allProviders.length > 0) return;
    this.isLoadingAllProviders = true;
    this.providerService
      .getAllProviders()
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          this.isLoadingAllProviders = false;
        })
      )
      .subscribe(
        (response: ProviderUploadDTO[]) => {
          this.allProviders = response;
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private getProviderNetworkDTOPayload() {
    let payload: ProviderNetworkDTO;

    const administrator = this.productNetworkFormGroup.get('administrator').value;
    const startDate = this.productNetworkFormGroup.get('startDate').value;
    const endDate = this.productNetworkFormGroup.get('endDate').value;

    const providersList: ProviderNetworkLinkDTO[] = this.selectedProviders.map((provider: ProviderDTO) => {
      const providerNetworkLinkDTOExist = this.selectedProviderNetworkLinkDTO.find((item) => item.provider.providerId === provider.providerId);

      if (providerNetworkLinkDTOExist) {
        return this.getProviderNetworkLinkDTOPayload(provider, providerNetworkLinkDTOExist);
      } else {
        return this.getProviderNetworkLinkDTOPayload(provider);
      }
    });

    payload = {
      providerNetworkId: this.isEditMode ? this.providerNetworkDTO.providerNetworkId : null,
      code: this.productNetworkFormGroup.get(Controls.Code).value,
      name: this.productNetworkFormGroup.get(Controls.Name).value,
      grading: this.productNetworkFormGroup.get(Controls.Grading).value,
      startDate: startDate ? new Date(startDate) : null,
      endDate: endDate ? new Date(endDate) : null,
      administrator: {
        value: this.productNetworkFormGroup.get(Controls.Administrator).value,
        name: this.administratorLoopUpValues.find(type => type.value === administrator).name
      },
      providers: providersList,
      created: this.isEditMode ? this.providerNetworkDTO.created : new Date(),
      createdBy: this.isEditMode ? this.providerNetworkDTO.createdBy : this.loggedInUserName,
      tenantId: this.isEditMode ? this.providerNetworkDTO.tenantId : null,
      updated: this.isEditMode ? new Date() : null,
      updatedBy: this.isEditMode ? this.loggedInUserName : null
    } as ProviderNetworkDTO;

    return payload;
  }

  private getProviderNetworkLinkDTOPayload(provider: ProviderDTO, providerNetworkLinkDTO?: ProviderNetworkLinkDTO): ProviderNetworkLinkDTO {
    let payload: ProviderNetworkLinkDTO;

    const isProviderAlreadyExistedInList = !!providerNetworkLinkDTO; // save original start and end dates
    let originalStartDate;
    let originalEndDate;

    if (isProviderAlreadyExistedInList) {
      originalStartDate = providerNetworkLinkDTO.startDate;
      originalEndDate = providerNetworkLinkDTO.endDate;
    }

    payload = {
      provider,
      startDate: isProviderAlreadyExistedInList ? originalStartDate : new Date(provider.startDate),
      endDate: isProviderAlreadyExistedInList ? originalEndDate : (provider.endDate != null ? new Date(provider.endDate) : null),
      created: this.isEditMode ? this.providerNetworkDTO.created : new Date(),
      createdBy: this.isEditMode ? this.providerNetworkDTO.createdBy : this.loggedInUserName,
      tenantId: this.isEditMode ? this.providerNetworkDTO.tenantId : null,
      updated: this.isEditMode ? new Date() : null,
      updatedBy: this.isEditMode ? this.loggedInUserName : null
    } as ProviderNetworkLinkDTO;
    return payload;
  }

  private setSelectedProviders() {
    if (
      (this.selectedProviders && this.selectedProviders.length > 0) &&
      (this.providerDataSource.data && this.providerDataSource.data.length > 0)
    ) {
      this.providerDataSource.data.forEach(pageProvider => {
        this.selectedProviders.forEach((selectedProvider: ProviderDTO) => {
          if (selectedProvider.providerId === pageProvider.providerId) {
            this.selectionControl.select(pageProvider);
          }
        });
      });
    }
  }

  private resetState() {
    this.fieldMapHashMap = {};
    this.clearSelection();
    this.clearSelectedProviders();
    this.IsProvidersEntryExist();
    this.resetProviderNetworkFormGroup();
  }

  private isFileValid(lines: string[], delimiter: string) {
    let isFileValid = true;
    if (lines[0].split(delimiter)[0]?.trim() !== 'isSelected' || lines[0].split(delimiter)[1]?.trim() !== 'providerId') {
      this.sharedService.openErrorDialog('The uploaded file does not have the expected columns.');
      isFileValid = false;
    }

    const errorList: string[] = [];
    errorList.push('isSelected;providerId;name');
    for (const line of lines) {
      if (!this.allRequiredFieldsHaveData(line, delimiter)) {
        errorList.push(`${line.split(delimiter)[0]};${line.split(delimiter)[1]};${line.split(delimiter)[2]}`);
        this.dialog.open(GenericListDialogComponent, {
          width: '40vw',
          data: {list: errorList, heading: 'Rows with Missing Required Fields'}
        });
        isFileValid = false;
        break;
      }
    }

    return isFileValid;
  }

  private allRequiredFieldsHaveData(line: string, delimiter: string): boolean {
    return (line.split(delimiter)[0] != null && line.split(delimiter)[0] !== '') && // isSelected
      (line.split(delimiter)[1] != null && line.split(delimiter)[1] !== ''); // providerId
  }
}
